import { useMutation } from "@apollo/client/react";
import {
  CopyFolderToExam,
  CopyFolderToExamVariables,
  GetParentStudyProgramme,
  GetParentStudyProgrammeVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { Loading } from "common/components/Loading";
import { NoDataError } from "common/components/NoDataError";
import { Section } from "common/components/Section";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import gql from "graphql-tag";
import * as React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { contentToSelectOption } from "../util";

const MUTATION = gql`
  mutation CopyFolderToExam($input: AddFolderToExamSyllabusInput!) {
    addFolderToExamSyllabus(input: $input) {
      examSyllabus {
        examDate
        title
        status
      }
    }
  }
`;

const GET_PARENT_STUDY_PROGRAMME = gql`
  query GetParentStudyProgramme($studyProgrammeId: ID!) {
    studyProgrammeById(id: $studyProgrammeId) {
      id
      title
      folderElements: elements(elementType: FOLDER_ELEMENT) {
        id
        content {
          id
          title
          status
        }
      }
    }
  }
`;

interface Props {
  examSyllabusId: string;
  studyProgrammeId: string;
  cancel(): void;
  complete(): void;
}

interface FormValues {
  folders: { value: string; label: string }[];
}

export const ExamSyllabusAddFolder: React.FC<
  React.PropsWithChildren<Props>
> = ({ examSyllabusId, studyProgrammeId, complete, cancel }) => {
  const methods = useForm<FormValues>();
  const [mutate, { error, loading }] = useMutation<
    CopyFolderToExam,
    CopyFolderToExamVariables
  >(MUTATION);

  const { data, loadingOrError } = useSimpleQuery<
    GetParentStudyProgramme,
    GetParentStudyProgrammeVariables
  >(GET_PARENT_STUDY_PROGRAMME, {
    variables: {
      studyProgrammeId,
    },
  });

  const onSubmit = async (input: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          examSyllabusId,
          folderIds: input.folders.map((f) => f.value),
        },
      },
    });

    if (result.data) {
      complete();
    }
  };

  if (loadingOrError) {
    return <Loading>{loadingOrError}</Loading>;
  }

  if (!data || !data.studyProgrammeById.folderElements) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }
  return (
    <Section title="Legg til undervisningsmateriell i pensumet">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Controller
            name="folders"
            control={methods.control}
            render={({ field: { onChange, value } }) => (
              <ReactSelect
                onChange={onChange}
                value={value}
                isMulti
                placeholder={`Velg undervisningsmateriell fra ${data.studyProgrammeById.title}`}
                options={data.studyProgrammeById.folderElements.map((e) =>
                  contentToSelectOption(e.content),
                )}
              />
            )}
          />
          <MutationLoadingOrError loading={loading} error={error} />
          <CancelOrSubmit
            onCancel={cancel}
            submitText="Legg til i pensum"
            isLoading={loading}
          />
        </form>
      </FormProvider>
    </Section>
  );
};
